/**
 * System Settings - Admin interface for system configuration management
 */

import React, { useState, useEffect } from 'react';
import { AdminService } from '../../services/admin';
import { Card } from '../../components/ui/Card';
import { Alert } from '../../components/ui/Alert';
import { LoadingSpinner } from '../../components/ui/LoadingSpinner';
import { Button } from '../../components/ui/Button';
import { Input } from '../../components/ui/Input';
import { Modal } from '../../components/ui/Modal';

interface SystemConfig {
  [key: string]: {
    value: any;
    description: string;
    is_public: boolean;
    updated_at: string | null;
  };
}

interface ConfigUpdateResult {
  updated_keys: string[];
  errors: Array<{ key: string; error: string }>;
  total_requested: number;
  successful: number;
  failed: number;
}

export function SystemSettings() {
  const [settings, setSettings] = useState<SystemConfig>({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  
  // Modal states
  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [editingKey, setEditingKey] = useState<string>('');
  
  // Form states
  const [newConfigKey, setNewConfigKey] = useState('');
  const [newConfigValue, setNewConfigValue] = useState('');
  const [newConfigDescription, setNewConfigDescription] = useState('');
  const [newConfigIsPublic, setNewConfigIsPublic] = useState(false);
  
  // Track pending changes
  const [pendingChanges, setPendingChanges] = useState<{ [key: string]: any }>({});

  useEffect(() => {
    loadSettings();
  }, []);

  const loadSettings = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const response = await AdminService.getSystemSettings(true); // include private settings
      setSettings(response.settings || {});
      setPendingChanges({});
    } catch (err: any) {
      console.error('Failed to load settings:', err);
      setError('Failed to load system settings. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleValueChange = (key: string, value: any) => {
    setPendingChanges(prev => ({
      ...prev,
      [key]: value
    }));
  };

  const resetPendingChanges = () => {
    setPendingChanges({});
    setSuccess(null);
    setError(null);
  };

  const saveChanges = async () => {
    if (Object.keys(pendingChanges).length === 0) {
      setError('No changes to save.');
      return;
    }

    try {
      setSaving(true);
      setError(null);
      setSuccess(null);

      const result: ConfigUpdateResult = await AdminService.updateSystemSettings(pendingChanges);
      
      if (result.failed > 0) {
        const errorMessages = result.errors.map(e => `${e.key}: ${e.error}`).join(', ');
        setError(`Some updates failed: ${errorMessages}`);
      }
      
      if (result.successful > 0) {
        setSuccess(`Successfully updated ${result.successful} setting(s).`);
        setPendingChanges({});
        await loadSettings(); // Reload to get fresh values
      }
    } catch (err: any) {
      console.error('Failed to save settings:', err);
      setError('Failed to save settings. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  const addNewSetting = async () => {
    if (!newConfigKey.trim() || !newConfigValue.trim()) {
      setError('Key and value are required.');
      return;
    }

    try {
      setSaving(true);
      setError(null);
      
      let parsedValue;
      try {
        parsedValue = JSON.parse(newConfigValue);
      } catch {
        parsedValue = newConfigValue; // Use as string if not valid JSON
      }

      const result: ConfigUpdateResult = await AdminService.updateSystemSettings({
        [newConfigKey]: parsedValue
      });

      if (result.successful > 0) {
        setSuccess(`Successfully added setting: ${newConfigKey}`);
        setShowAddModal(false);
        resetForm();
        await loadSettings();
      } else if (result.failed > 0) {
        const error = result.errors[0];
        setError(`Failed to add setting: ${error.error}`);
      }
    } catch (err: any) {
      console.error('Failed to add setting:', err);
      setError('Failed to add setting. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  const resetForm = () => {
    setNewConfigKey('');
    setNewConfigValue('');
    setNewConfigDescription('');
    setNewConfigIsPublic(false);
  };

  const formatValue = (value: any): string => {
    if (typeof value === 'object') {
      return JSON.stringify(value, null, 2);
    }
    return String(value);
  };

  const parseValue = (valueStr: string): any => {
    try {
      return JSON.parse(valueStr);
    } catch {
      return valueStr;
    }
  };

  const formatTimestamp = (timestamp: string | null) => {
    if (!timestamp) return 'Never';
    return new Date(timestamp).toLocaleString();
  };

  const getValueType = (value: any): string => {
    if (value === null) return 'null';
    if (Array.isArray(value)) return 'array';
    return typeof value;
  };

  const getTypeColor = (type: string): string => {
    switch (type) {
      case 'string': return 'text-green-400';
      case 'number': return 'text-blue-400';
      case 'boolean': return 'text-purple-400';
      case 'object': return 'text-yellow-400';
      case 'array': return 'text-pink-400';
      default: return 'text-gray-400';
    }
  };

  if (loading) {
    return (
      <div className="p-8 bg-gray-900 min-h-screen">
        <div className="max-w-7xl mx-auto">
          <h1 className="text-3xl font-bold text-white mb-8">System Settings</h1>
          <div className="flex items-center justify-center h-64">
            <LoadingSpinner size="lg" />
          </div>
        </div>
      </div>
    );
  }

  const settingsArray = Object.entries(settings);
  const hasChanges = Object.keys(pendingChanges).length > 0;

  return (
    <div className="p-8 bg-gray-900 min-h-screen">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="text-3xl font-bold text-white">System Settings</h1>
            <p className="text-gray-400 mt-1">
              Manage system configuration and application settings
            </p>
          </div>
          <div className="flex gap-4">
            <Button 
              variant="secondary"
              onClick={() => setShowAddModal(true)}
            >
              Add Setting
            </Button>
            {hasChanges && (
              <>
                <Button 
                  variant="secondary"
                  onClick={resetPendingChanges}
                >
                  Reset Changes
                </Button>
                <Button 
                  variant="primary"
                  onClick={saveChanges}
                  disabled={saving}
                >
                  {saving ? 'Saving...' : `Save Changes (${Object.keys(pendingChanges).length})`}
                </Button>
              </>
            )}
          </div>
        </div>

        {error && (
          <Alert
            variant="error"
            title="Error"
            className="mb-6"
          >
            {error}
          </Alert>
        )}

        {success && (
          <Alert
            variant="success"
            title="Success"
            className="mb-6"
          >
            {success}
          </Alert>
        )}

        {/* Pending Changes Indicator */}
        {hasChanges && (
          <Alert
            variant="info"
            title="Unsaved Changes"
            className="mb-6"
          >
            You have {Object.keys(pendingChanges).length} unsaved change(s). Don't forget to save your changes.
          </Alert>
        )}

        {/* Settings List */}
        <div className="space-y-4">
          {settingsArray.length === 0 ? (
            <Card className="p-8 text-center">
              <div className="text-gray-400">
                No system settings found. Add your first setting using the button above.
              </div>
            </Card>
          ) : (
            settingsArray.map(([key, config]) => {
              const currentValue = pendingChanges.hasOwnProperty(key) ? pendingChanges[key] : config.value;
              const hasLocalChange = pendingChanges.hasOwnProperty(key);
              
              return (
                <Card key={key} className={`p-6 ${hasLocalChange ? 'border-blue-500 border-2' : ''}`}>
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <div className="flex items-center gap-3 mb-2">
                        <h3 className="text-lg font-semibold text-white">
                          {key}
                        </h3>
                        <span className={`text-xs px-2 py-1 rounded ${getTypeColor(getValueType(currentValue))}`}>
                          {getValueType(currentValue)}
                        </span>
                        {!config.is_public && (
                          <span className="text-xs px-2 py-1 rounded bg-red-900 text-red-300">
                            PRIVATE
                          </span>
                        )}
                        {hasLocalChange && (
                          <span className="text-xs px-2 py-1 rounded bg-blue-900 text-blue-300">
                            MODIFIED
                          </span>
                        )}
                      </div>
                      
                      {config.description && (
                        <p className="text-gray-400 text-sm mb-3">
                          {config.description}
                        </p>
                      )}

                      <div className="space-y-2">
                        <div className="text-sm text-gray-500">
                          Current Value:
                        </div>
                        <div className="bg-gray-800 p-3 rounded font-mono text-sm">
                          {getValueType(currentValue) === 'object' || getValueType(currentValue) === 'array' ? (
                            <textarea
                              value={formatValue(currentValue)}
                              onChange={(e) => handleValueChange(key, parseValue(e.target.value))}
                              className="w-full bg-transparent text-white resize-none border-none outline-none"
                              rows={Math.min(formatValue(currentValue).split('\n').length, 10)}
                            />
                          ) : (
                            <input
                              type={getValueType(config.value) === 'boolean' ? 'checkbox' : 'text'}
                              checked={getValueType(currentValue) === 'boolean' ? Boolean(currentValue) : undefined}
                              value={getValueType(currentValue) === 'boolean' ? undefined : String(currentValue)}
                              onChange={(e) => {
                                const value = getValueType(config.value) === 'boolean' 
                                  ? e.target.checked 
                                  : e.target.value;
                                handleValueChange(key, value);
                              }}
                              className="w-full bg-transparent text-white border-none outline-none"
                            />
                          )}
                        </div>
                      </div>

                      <div className="text-xs text-gray-500 mt-2">
                        Last updated: {formatTimestamp(config.updated_at)}
                      </div>
                    </div>
                  </div>
                </Card>
              );
            })
          )}
        </div>

        {/* Add Setting Modal */}
        <Modal
          isOpen={showAddModal}
          onClose={() => {
            setShowAddModal(false);
            resetForm();
          }}
          title="Add New Setting"
        >
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">
                Setting Key *
              </label>
              <Input
                type="text"
                value={newConfigKey}
                onChange={(e) => setNewConfigKey(e.target.value)}
                placeholder="e.g., max_concurrent_runs"
                className="bg-gray-800"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">
                Value * (JSON format for objects/arrays)
              </label>
              <textarea
                value={newConfigValue}
                onChange={(e) => setNewConfigValue(e.target.value)}
                placeholder='e.g., "string", 123, true, {"key": "value"}, ["item1", "item2"]'
                rows={4}
                className="w-full bg-gray-800 border border-gray-600 rounded px-3 py-2 text-white font-mono text-sm resize-none"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">
                Description
              </label>
              <textarea
                value={newConfigDescription}
                onChange={(e) => setNewConfigDescription(e.target.value)}
                placeholder="Brief description of this setting..."
                rows={2}
                className="w-full bg-gray-800 border border-gray-600 rounded px-3 py-2 text-white resize-none"
              />
            </div>

            <div>
              <label className="flex items-center gap-2">
                <input
                  type="checkbox"
                  checked={newConfigIsPublic}
                  onChange={(e) => setNewConfigIsPublic(e.target.checked)}
                  className="rounded"
                />
                <span className="text-gray-300">Public setting (visible to non-admin users)</span>
              </label>
            </div>
            
            <div className="flex gap-3 pt-4">
              <Button
                variant="primary"
                onClick={addNewSetting}
                disabled={!newConfigKey.trim() || !newConfigValue.trim() || saving}
              >
                {saving ? 'Adding...' : 'Add Setting'}
              </Button>
              <Button
                variant="secondary"
                onClick={() => {
                  setShowAddModal(false);
                  resetForm();
                }}
              >
                Cancel
              </Button>
            </div>
          </div>
        </Modal>
      </div>
    </div>
  );
}